import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eraser, Loader2, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';

/**
 * "Remove background" for the THREE logo slots (merchant logo, scratch logo,
 * org/voucher logo). Never offered for wallpapers or gift photos — those are
 * full-bleed images with no background box, and the server rejects those kinds.
 *
 * Nothing is applied until the operator looks at the result and taps Use this:
 * the endpoint returns a NEW file and leaves the original untouched, so the
 * preview is the real processed PNG on a checkerboard, not a simulation.
 */
export const RemoveBackgroundButton = ({ url, kind, onApply, testId }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    if (!url) return null;

    const run = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await api.post('/upload/remove-white-background', { url, kind });
            setResult(res.data);
        } catch (e) {
            setError(e?.response?.data?.detail || t('logoBg.failed'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button type="button" variant="ghost" size="sm" onClick={run} disabled={busy}
                    data-testid={testId} className="text-muted-foreground hover:text-emerald-600">
                {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eraser className="w-3 h-3 mr-1" />}
                {t('logoBg.action')}
            </Button>

            {error && (
                <span className="text-[11px] text-red-500" data-testid={`${testId}-error`}>{error}</span>
            )}

            {result && (
                <div className="w-full mt-2 rounded-lg border border-border bg-background p-3 space-y-3"
                     data-testid={`${testId}-preview`}>
                    <p className="text-xs font-semibold">{t('logoBg.previewTitle')}</p>
                    <div className="flex items-center gap-4">
                        <figure className="space-y-1">
                            <img src={url} alt="" className="h-20 w-20 object-contain rounded bg-slate-900" />
                            <figcaption className="text-[10px] text-muted-foreground text-center">
                                {t('logoBg.before')}
                            </figcaption>
                        </figure>
                        <figure className="space-y-1">
                            {/* Checkerboard: transparency has to be VISIBLE to be trusted. */}
                            <img src={result.url} alt="" data-testid={`${testId}-preview-img`}
                                 className="h-20 w-20 object-contain rounded"
                                 style={{
                                     backgroundImage:
                                         'linear-gradient(45deg, #cbd5e1 25%, transparent 25%),' +
                                         'linear-gradient(-45deg, #cbd5e1 25%, transparent 25%),' +
                                         'linear-gradient(45deg, transparent 75%, #cbd5e1 75%),' +
                                         'linear-gradient(-45deg, transparent 75%, #cbd5e1 75%)',
                                     backgroundSize: '10px 10px',
                                     backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
                                 }} />
                            <figcaption className="text-[10px] text-muted-foreground text-center">
                                {t('logoBg.after')}
                            </figcaption>
                        </figure>
                        <p className="text-[11px] text-muted-foreground flex-1" data-testid={`${testId}-stats`}>
                            {t('logoBg.removedStat', { pct: result.removed_pct, colour: result.removed_colour })}
                        </p>
                    </div>
                    {result.warning && (
                        <p className="text-[11px] text-amber-600" data-testid={`${testId}-warning`}>
                            {t(`logoBg.warning.${result.warning}`)}
                        </p>
                    )}
                    <div className="flex items-center gap-2">
                        {result.warning !== 'all_removed' && (
                        <Button type="button" size="sm" onClick={() => { onApply(result.url); setResult(null); }}
                                data-testid={`${testId}-apply`}>
                            <Check className="w-3 h-3 mr-1" /> {t('logoBg.use')}
                        </Button>
                        )}
                        <Button type="button" size="sm" variant="ghost" onClick={() => setResult(null)}
                                data-testid={`${testId}-cancel`}>
                            <X className="w-3 h-3 mr-1" /> {t('logoBg.keepOriginal')}
                        </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{t('logoBg.saveHint')}</p>
                </div>
            )}
        </>
    );
};

export default RemoveBackgroundButton;
